"use client";

import Image from "next/image";
import Logo from "@/app/logo.svg";
import { useAuth } from '@/hooks/use-auth';
import { 
  Building2, 
  FolderTree, 
  Trash2, 
  Users, 
  ShieldCheck, 
  Settings,
  LogOut,
  ChevronDown,
  Share2,
  ScrollText,
  Mails,
  Plus,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useEffect, useState } from 'react';
import { dmsApi } from '@/lib/api';
import { toast } from '@/hooks/use-toast';

interface SidebarNavProps {
  onSelectFolder: (id: string) => void;
  onSelectDepartment?: (departmentId: string, departmentName?: string) => void;
  activeView: 'all' | 'trash' | 'shared' | 'licenses' | 'correspondence' | 'users' | 'roles' | 'settings';
  onSelectView: (view: SidebarNavProps['activeView']) => void;
}

export function SidebarNav({ onSelectFolder, onSelectDepartment, activeView, onSelectView }: SidebarNavProps) {
  const { user, logout, isSuperAdmin, hasPermission } = useAuth();
  const [departments, setDepartments] = useState<any[]>([]);
  const [activeDept, setActiveDept] = useState<any>(null);
  const [addDeptOpen, setAddDeptOpen] = useState(false);
  const [deptMenuOpen, setDeptMenuOpen] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [creatingDept, setCreatingDept] = useState(false);

  const canCreateDepartment = isSuperAdmin || hasPermission('create_department');
  const canSwitchDepartment = isSuperAdmin;

  const openAddDepartmentDialog = () => {
    setDeptMenuOpen(false);
    // Let the dropdown finish closing before opening the dialog (avoids stuck pointer-events).
    window.setTimeout(() => setAddDeptOpen(true), 50);
  };

  useEffect(() => {
    const fetchDepts = async () => {
      try {
        const depts = await dmsApi.departments.list();
        setDepartments(depts);

        const rawDeptId = user?.departmentId as string | { _id?: string } | undefined;
        const userDeptId =
          typeof rawDeptId === 'object' && rawDeptId?._id
            ? String(rawDeptId._id)
            : typeof rawDeptId === 'string'
              ? rawDeptId
              : '';

        if (userDeptId) {
          const myDept = depts.find((d) => String(d._id) === String(userDeptId));
          if (myDept) {
            await handleSelectDept(myDept);
            return;
          }
        }
        if (depts.length > 0) {
          await handleSelectDept(depts[0]);
        }
      } catch (err) {
        console.error("Failed to fetch departments", err);
      }
    };
    void fetchDepts();
  }, [user]);

  const handleSelectDept = async (dept: any) => {
    if (!dept?._id) return;
    setDeptMenuOpen(false);
    setActiveDept(dept);
    onSelectDepartment?.(String(dept._id), dept.name);
    try {
      const root = await dmsApi.departments.getRootFolder(String(dept._id));
      onSelectFolder(root._id);
    } catch (err) {
      console.error("Failed to get root folder", err);
      toast({
        variant: 'destructive',
        title: 'Could not open department',
        description: (err as any)?.error || (err as any)?.message || 'Failed to load folder',
      });
    }
  };

  const handleSelectAllFiles = async () => {
    if (activeDept?._id) {
      await handleSelectDept(activeDept);
    }
    onSelectView('all');
  };

  const handleCreateDepartment = async () => {
    const name = newDeptName.trim();
    if (!name) {
      toast({ variant: 'destructive', title: 'Department name is required' });
      return;
    }
    setCreatingDept(true);
    try {
      const created = await dmsApi.departments.create({ name });
      const depts = await dmsApi.departments.list();
      setDepartments(depts);
      setAddDeptOpen(false);
      setNewDeptName('');
      // Clear any leftover modal lock from dialog/dropdown combo
      document.body.style.pointerEvents = '';
      toast({ title: 'Department created', description: `"${created.name}" is ready to use.` });
      const selected =
        depts.find((d) => String(d._id) === String(created._id)) || created;
      await handleSelectDept(selected);
      onSelectView('all');
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Could not create department',
        description: err?.error || err?.message || 'Unknown error',
      });
    } finally {
      setCreatingDept(false);
    }
  };

  return (
    <div className="w-72 h-screen flex flex-col bg-white border-r border-border">
      <div className="p-6 flex items-center gap-3">
        <Image src={Logo} alt="CoopXL Docs" className="h-8 w-auto" priority />
      </div>

      <div className="flex-1 overflow-auto px-4 py-2 space-y-6">
        <div className="space-y-2">
          <div className="px-2 flex items-center justify-between gap-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Department</p>
            {canCreateDepartment && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-1.5 text-xs text-muted-foreground hover:text-foreground"
                onClick={openAddDepartmentDialog}
              >
                <Plus size={14} className="mr-0.5" />
                Add
              </Button>
            )}
          </div>
          <DropdownMenu open={deptMenuOpen} onOpenChange={setDeptMenuOpen} modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between h-11 px-3 bg-secondary/30 border-none shadow-none text-left"
                disabled={!canSwitchDepartment && !canCreateDepartment}
              >
                <div className="flex items-center gap-2 truncate">
                  <Building2 size={16} className="text-primary" />
                  <span className="truncate font-medium">{activeDept?.name || "Select Dept"}</span>
                </div>
                <ChevronDown size={14} className="text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            {(canSwitchDepartment || canCreateDepartment) && (
              <DropdownMenuContent className="w-[calc(18rem-2rem)]" align="start">
                {canSwitchDepartment ? (
                  departments.map((dept) => (
                    <DropdownMenuItem
                      key={String(dept._id)}
                      onSelect={() => {
                        void handleSelectDept(dept);
                      }}
                    >
                      {dept.name}
                      {activeDept && String(activeDept._id) === String(dept._id) ? ' ✓' : ''}
                    </DropdownMenuItem>
                  ))
                ) : (
                  <DropdownMenuItem disabled>
                    {activeDept?.name || 'Current department'}
                  </DropdownMenuItem>
                )}
                {canCreateDepartment && (
                  <>
                    {canSwitchDepartment && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        openAddDepartmentDialog();
                      }}
                    >
                      <Plus size={14} className="mr-2" />
                      Add department
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            )}
          </DropdownMenu>
        </div>

        <div className="space-y-1">
          <p className="px-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Documents</p>
          <NavItem 
            icon={<FolderTree size={18} />} 
            label="All Files" 
            active={activeView === 'all'} 
            onClick={handleSelectAllFiles}
          />
          <NavItem 
            icon={<Trash2 size={18} />} 
            label="Trash" 
            active={activeView === 'trash'} 
            onClick={() => onSelectView('trash')}
          />
          <NavItem 
            icon={<Share2 size={18} />} 
            label="Shared with me" 
            active={activeView === 'shared'} 
            onClick={() => onSelectView('shared')}
          />
          {(isSuperAdmin || hasPermission('view_license')) && (
            <NavItem
              icon={<ScrollText size={18} />}
              label="Documents"
              active={activeView === 'licenses'}
              onClick={() => {
                if (activeDept?._id) {
                  onSelectDepartment?.(activeDept._id, activeDept.name);
                }
                onSelectView('licenses');
              }}
            />
          )}
          {(isSuperAdmin || hasPermission('view_correspondence')) && (
            <NavItem
              icon={<Mails size={18} />}
              label="Incoming & Outgoing"
              active={activeView === 'correspondence'}
              onClick={() => {
                if (activeDept?._id) {
                  onSelectDepartment?.(activeDept._id, activeDept.name);
                }
                onSelectView('correspondence');
              }}
            />
          )}
        </div>

        {(isSuperAdmin || hasPermission('view_user')) && (
          <div className="space-y-1">
            <p className="px-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Admin</p>
            {isSuperAdmin && (
              <NavItem 
                icon={<Users size={18} />} 
                label="Users" 
                active={activeView === 'users'} 
                onClick={() => onSelectView('users')}
              />
            )}
            <NavItem 
              icon={<ShieldCheck size={18} />} 
              label="Roles" 
              active={activeView === 'roles'} 
              onClick={() => onSelectView('roles')}
            />
            <NavItem 
              icon={<Settings size={18} />} 
              label="Settings" 
              active={activeView === 'settings'} 
              onClick={() => onSelectView('settings')}
            />
          </div>
        )}
      </div>

      <div className="p-4 border-t">
        <div className="flex items-center gap-3 p-2 rounded-xl bg-secondary/20 mb-2">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
            {user?.name?.[0] || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{user?.name || "User"}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <Button variant="ghost" className="w-full justify-start text-destructive hover:bg-destructive/10" onClick={logout}>
          <LogOut size={16} className="mr-2" />
          Log out
        </Button>
      </div>

      <Dialog
        open={addDeptOpen}
        onOpenChange={(open) => {
          setAddDeptOpen(open);
          if (!open) {
            setNewDeptName('');
            document.body.style.pointerEvents = '';
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add department</DialogTitle>
            <DialogDescription>
              Create a new department. A root folder will be created automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="new-department-name">Name</Label>
            <Input
              id="new-department-name"
              value={newDeptName}
              onChange={(e) => setNewDeptName(e.target.value)}
              placeholder="e.g. Finance"
              disabled={creatingDept}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleCreateDepartment();
                }
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDeptOpen(false)} disabled={creatingDept}>
              Cancel
            </Button>
            <Button onClick={() => void handleCreateDepartment()} disabled={creatingDept || !newDeptName.trim()}>
              {creatingDept ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NavItem({ icon, label, active = false, onClick }: { icon: React.ReactNode, label: string, active?: boolean, onClick?: () => void }) {
  return (
    <Button 
      variant="ghost" 
      onClick={onClick}
      className={cn(
        "w-full justify-start gap-3 h-10 font-medium transition-all",
        active ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/20" : "text-muted-foreground hover:bg-secondary"
      )}
    >
      {icon}
      {label}
    </Button>
  );
}
